const Fetcher = require('od-modules/utils/fetcher');
const logger = require('od-modules/utils/logger');
const availableProducts = {
    none: 'none',
    bags: 'bags',
    seats: 'seats',
};

const getTodayUTC = () => {
    const localDate = new Date();
    return Date.UTC(
        localDate.getUTCFullYear(),
        localDate.getUTCMonth(),
        localDate.getUTCDate(),
        localDate.getUTCHours(),
        localDate.getUTCMinutes(),
        localDate.getUTCSeconds()
    );
};

const TODAY_UTC = getTodayUTC();
const ONE_DAY = 24 * 60 * 60 * 1000;
const ADULTS_KEY = 'Adults';
const CHILDREN_AGES_KEY = 'ChildrenAges';

const getReturnItineraryDeparture = (booking) => (
    Odigeo._.first(Odigeo._.last(Odigeo._.get(booking, 'itineraryBookings.itinerarySegments')).sections).section ?? 0
);

const getDateBefore = (date) => {
    const dateBefore = new Date(date);
    dateBefore.setDate(dateBefore.getDate() - 1);
    return dateBefore;
};

const hasExpiredDepartureDay = (booking) => {
    if (Odigeo._.has(booking, 'itineraryBookings')) {
        return getDateBefore(getReturnItineraryDeparture(booking).departureDate).getTime() < TODAY_UTC;
    }
    return true;
};

const parsePostBookingParams = (params) => {
    if (Odigeo._.isEmpty(params)) {
        return;
    }

    const {trackingId, bookingId, product: _product} = Odigeo.Utils.parseHashParameters(params);
    const product = getProductFromParam(availableProducts, _product);

    if (Odigeo._.isEmpty(trackingId) || Odigeo._.isEmpty(bookingId)) {
        return;
    }

    try {
        return {
            email: atob(trackingId),
            bookingId,
            product,
        };
    } catch (error) {
        logger.error(error);
    }
};

const getProductFromParam = (availableProducts, productParam) => {
    const serializedAvailableProducts = Odigeo._.values(availableProducts).join('|');
    const availableProductsRegex = new RegExp(`^(${serializedAvailableProducts})`);
    const product = Odigeo._.isEmpty(productParam) ? '' : Odigeo._.first(productParam.match(availableProductsRegex));
    return Odigeo._.isEmpty(product) ? availableProducts.none : product;
};

const getCollectionMethods = (ancillaryInfo) => {
    return Odigeo._.map(ancillaryInfo.collectionMethods, ({collectionMethodType, creditCardType}) => {
        return {
            method: collectionMethodType,
            code: creditCardType.code,
            name: creditCardType.name,
        };
    });
};

const getHotelsWhiteLabel = () => {
    const protocol = 'https://';
    const brands = {ED: 'edreams', GV: 'govoyages', OP: 'opodo', TL: 'travellink'};
    const brand = brands[window.Odigeo.SessionScope.brand];
    const hotelLabel = brand === 'govoyages' ? 'hotel' : 'hotels';

    return `${protocol}${hotelLabel}.${brand}.com`;
};

const getBookingWLFallbackLabel = () => {
    const PREFIXES_BY_BRAND = {ED: 'edr', GV: 'gvy', OP: 'opo', TL: 'tl'};
    const prefix = PREFIXES_BY_BRAND[Odigeo.SessionScope.brand];
    const website = Odigeo.SessionScope.website.toLowerCase();
    const touchpoint = getBuyPathFromUrl()?.toLowerCase() || 'fallback';
    const platform = Odigeo.SessionScope.flow;
    return `${prefix}-link-${website}-${touchpoint}-${platform}`; // For example, edr-link-es-hsa_pb_yourjourney-desktop
};

const getCreditCardTypeCodes = (collectionMethods) => {
    return Odigeo._.map(collectionMethods, 'code');
};

const isAcceptedCreditCardType = (collectionMethods, creditCardTypeCode) => {
    const acceptedCreditCardTypeCodes = getCreditCardTypeCodes(collectionMethods);
    return !Odigeo._.isEmpty(creditCardTypeCode) && Odigeo._.includes(acceptedCreditCardTypeCodes, creditCardTypeCode);
};

const getTouchPointParams = (isAccommodationPostBookingCoSearch, parameters) => {
    let wapb3;
    let label;
    if (isAccommodationPostBookingCoSearch) {
        wapb3 = Odigeo.UIElements.translations.hotelPostBookingTouchpoints.wapb3;
        label = Odigeo.UIElements.translations.hotelPostBookingTouchpoints.label;
    } else {
        wapb3 = parameters.wapb3;
        label = parameters.label;
    }
    return {
        wapb3,
        label,
    };
};

const selectAncillariesAndAddToShoppingBasket = (oneClickCheckoutContainer, bookingId, ancillarySelectionRequestList, productIdProperty, onFailure) =>
    oneClickCheckoutContainer.get('getShoppingBasketId')
        .then(Odigeo._.partial(getSelectedAncillariesProductId, _, bookingId, ancillarySelectionRequestList))
        .then(Odigeo._.partial(addProductToShoppingBasket, _, oneClickCheckoutContainer, productIdProperty))
        .catch(onFailure);

const getSelectedAncillariesProductId = (shoppingBasketId, bookingId, ancillarySelectionRequestList) => {
    return Fetcher.post(`${Odigeo.Settings.getProperty('postBookingServiceUrlFormatter')('selectAncillary')}/${bookingId}`, {
        shoppingBasketId,
        ancillarySelectionRequestList,
    }).json();
};

const initOneClickCheckout = (oneClickCheckoutContainer, bookingInfo, abTest) => {
    if (abTest) {
        oneClickCheckoutContainer.execute('setBuyerInfo', Odigeo._.get(bookingInfo, 'buyer'));
        oneClickCheckoutContainer.execute('setBundleId', Odigeo._.get(bookingInfo, 'shoppingBasketBundle.id'));
    }
};

const hideOneClickCheckoutOnShoppingPhase = (oneClickCheckoutContainer, widget, shoppingPhase, abTest) => {
    if (abTest) {
        widget.listenTo(widget.model, 'change:phase', (model, phase) => {
            if (phase === shoppingPhase) {
                oneClickCheckoutContainer.execute('hideAllLayers');
            }
        });
    }
};

const addProductToShoppingBasket = (response, oneClickCheckoutContainer, productIdProperty) => {
    if (Odigeo._.get(response, 'success')) {
        return oneClickCheckoutContainer.get('requestAddProductToShoppingBasket', Odigeo._.get(response, productIdProperty));
    } else {
        throw new Error(Odigeo._.get(response, 'code', ''));
    }
};

const getTravellers = (travellers) => {
    const {adults, childrenAges} = travellers.reduce((state, current) => {
        if (current.travellerType === 'ADULT') {
            state.adults++;
        } else {
            const age = Odigeo.Utils.getAge(current.dateOfBirth) || 0;
            state.childrenAges.push(age);
        }
        return state;
    }, {adults: 0, childrenAges: []});

    return {adults, childrenAges};
};

const getFormattedDate = (date) => {
    return Odigeo.Utils.formatDate(date, Odigeo.Utils.formatDate.masks.isoDate, true);
};

const getDaysBetween = (arrivalDate, departureDate) => {
    const timeDifference = departureDate - arrivalDate;
    return Math.abs(timeDifference / ONE_DAY);
};

const prepareCheckInDate = (arrivalDate) => (
    !arrivalDate || (TODAY_UTC > arrivalDate) ? TODAY_UTC : arrivalDate
);

const getCheckInDate = (arrivalDate) => {
    const checkInDate = prepareCheckInDate(arrivalDate);
    return getFormattedDate(checkInDate);
};

const getCheckOutDate = (tripType, itinerarySegments, arrivalDate) => {
    const checkInDate = prepareCheckInDate(arrivalDate);
    if (tripType === 'ONE_WAY') {
        return getFormattedDate(checkInDate + ONE_DAY);
    }

    let departureDate;
    if (tripType === 'ROUND_TRIP') {
        const returnFlight = Odigeo._.first(Odigeo._.last(itinerarySegments).sections).section;
        departureDate = returnFlight.departureDate;
    } else {
        const returnFlight = itinerarySegments[1].sections[0].section;
        departureDate = returnFlight.departureDate;
    }

    const daysBetween = getDaysBetween(checkInDate, departureDate);
    if (daysBetween < 1 || daysBetween > 7) {
        departureDate = checkInDate + ONE_DAY;
    }
    return getFormattedDate(departureDate);
};

const getRoomsParam = (_travellers) => {
    const {adults, childrenAges} = getTravellers(_travellers);

    const _adults = `room0${ADULTS_KEY}=${adults}`;
    const _children = childrenAges.length > 0 ? `;room0${CHILDREN_AGES_KEY}=${childrenAges.join('-')}`: '';

    return `${_adults}${_children}`;
};

const getHashToHSA = (email, creationDate, isPrimeUser = false, bookingId) => {
    if (!email || !creationDate) {
        return '';
    }
    const primeInfo = isPrimeUser ? 'PRIME' : 'NO_PRIME';
    const bookingDetail = `${email}|${creationDate}|${primeInfo}|${bookingId}`;
    return btoa(bookingDetail);
};

const getBuyPathFromUrl = () => {
    const hash = window.location.hash;
    const params = Odigeo.Utils.parseHashParameters(hash);
    return params.buyPath || '';
};

const errorHotelsHomeRedirection = (delay = true) => {
    if (delay) {
        setTimeout(() => {
            Odigeo.trigger('frontend-shell:navigateHome');
        },
        5000);
    } else {
        Odigeo.trigger('frontend-shell:navigateHome');
    }
};

const triggerHotelPostBookingCoSearch = (isAccommodationPostBookingCoSearch, bookingInfo) => {
    Odigeo.Session.set('isAccommodationPostBookingCoSearch', false);
    Odigeo.trigger('post-booking:getTripFromSessionOk', {
        bookingDetailDTO: bookingInfo,
        isAccommodationPostBookingCoSearch,
    });
};

const setBookingTrackingLabelInSessionIfPresent = ({label}) => {
    if (label) {
        Odigeo.Session.set('bookingTrackingParameterLabel', label);
    }
};

module.exports = {
    ONE_DAY,
    TODAY_UTC,
    hasExpiredDepartureDay,
    getFormattedDate,
    getProductFromParam,
    availableProducts,
    parsePostBookingParams,
    getCollectionMethods,
    getHotelsWhiteLabel,
    getCreditCardTypeCodes,
    isAcceptedCreditCardType,
    getTouchPointParams,
    initOneClickCheckout,
    hideOneClickCheckoutOnShoppingPhase,
    selectAncillariesAndAddToShoppingBasket,
    getCheckInDate,
    getCheckOutDate,
    getRoomsParam,
    getHashToHSA,
    getBuyPathFromUrl,
    errorHotelsHomeRedirection,
    triggerHotelPostBookingCoSearch,
    getBookingWLFallbackLabel,
    setBookingTrackingLabelInSessionIfPresent,
};
